import { ofetch } from 'ofetch'
import { stringify } from 'qs'
import pluralize from 'pluralize'
import { camelCase } from 'case-anything'
import { preview } from 'vite'

type SearchQuery<Query = { [key: string]: any }> = {
  $page?: number
  $limit?: number
} & Query

export const useApi = () => {
  const runtimeConfig = useRuntimeConfig()
  const auth = useAuth()
  const adonisApi = ofetch.create({
    baseURL: runtimeConfig.public.apiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    async onRequest({ request, options }) {
      if (auth.isAuthenticated) {
        options.headers = {
          ...options.headers,
          Authorization: `Bearer ${auth.jwt}`,
        }
      }

      if (options.query || options.params) {
        let convertedOptions = new URLSearchParams(
          stringify(options.query ?? options.params),
        )
        options.query = Object.fromEntries(convertedOptions)
        options.params = Object.fromEntries(convertedOptions)
      }
    },
  })

  const makeCrudMethods = <S extends SearchQuery>(path: string) => {
    return {
      find: (query?: S) => adonisApi(path, { params: query }),
      findOne: (query?: S) =>
        adonisApi(path, { params: query }).then(
          (res) => res.data?.[0] ?? res?.[0],
        ),
      create: (body: any) => adonisApi(path, { method: 'POST', body }),
      update: (id: string, body: any) =>
        adonisApi(`${path}/${id}`, { method: 'PATCH', body }),
      get: (id: string) => adonisApi(`${path}/${id}`),
      delete: (id: string) => adonisApi(`${path}/${id}`, { method: 'DELETE' }),
    }
  }

  return {
    service(name: string) {
      return (
        this[camelCase(pluralize(name)) as keyof ReturnType<typeof useApi>] ??
        this[name as keyof ReturnType<typeof useApi>]
      )
    },
    resource(resourceRef: string): any {
      const [name, id] = resourceRef.split(':')
      return this.service(name).get(id)
    },
    users: {
      me: () => adonisApi('/users/me'),
      find: () => adonisApi('/users/me'),
      // get: (id: string) => adonisApi(`/users/${id}`),
    },
    auth: {
      msalCallback: (body: { accessToken: string }) =>
        adonisApi('/msal-callback', { method: 'POST', body }),
    },

    collaborators: {
      ...makeCrudMethods('/collaborators'),
      getByCode: (code: string) => adonisApi(`/collaborators/code/${code}`),
      findByEmail: ({ email }: { email: string }) =>
        adonisApi(`/collaborators/email/${email}`),
      import: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return adonisApi('/collaborators/import', {
          method: 'POST',
          body: formData,
        })
      },
    },
    tenants: {
      ...makeCrudMethods('/tenants'),
      getBySiret: (siret: string) => adonisApi(`/tenants/siret/${siret}`),
      affiliate: (id: string) =>
        adonisApi(`/tenants/affiliate/${id}`, {
          method: 'PATCH',
        }),
      search: (search: string) =>
        adonisApi(`/tenants/search/${search}`, {
          method: 'GET',
        }),
    },
    redirects: makeCrudMethods('/redirects'),
    activities: makeCrudMethods('/activities'),
    entities: {
      ...makeCrudMethods('/entities'),
      export: (ids: string[]) =>
        adonisApi(`/entities/export`, {
          method: 'POST',
          body: {
            ids,
          },
          responseType: 'blob',
        }),
      import: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return adonisApi('/entities/import', {
          method: 'POST',
          body: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      },
    },
    integrations: {
      ...makeCrudMethods('/integrations'),
      execute: (id: string, action: string, body: any) =>
        adonisApi(`/integrations/${id}/${action}`, { method: 'POST', body }),
    },
    public: {
      get: () => adonisApi('/public-config'),
    },

    uploads: {
      uploadImage: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return adonisApi('/uploads', {
          method: 'POST',
          body: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      },
    },

    argos: {
      searchCompany: (query: string) => {
        return adonisApi('/argos/compagny', {
          method: 'GET',
          params: { search: query },
        })
      },
    },
  }
}
